import { useEffect, useState } from "react";

/**
 * Hides a sticky element when the user scrolls DOWN, reveals it when scrolling UP.
 * Used by both the mobile Header and the mobile sticky add-to-cart bar so they
 * disappear together for a clean, distraction-free reading flow.
 *
 * Options:
 *  - topThreshold:    always-visible band at the top of the page (px)
 *  - bottomThreshold: always-visible band near the end of the page (px)
 *  - delta:           min Δ in px between events to flip state (anti-jitter)
 */
export default function useScrollDirection({
  topThreshold = 80,
  bottomThreshold = 120,
  delta = 6,
} = {}) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    let last = window.scrollY;
    let raf = 0;
    const onScroll = () => {
      if (raf) return;
      raf = requestAnimationFrame(() => {
        const y = window.scrollY;
        const dy = y - last;
        const nearBottom =
          window.innerHeight + y >=
          document.documentElement.scrollHeight - bottomThreshold;
        if (y < topThreshold || nearBottom) {
          setVisible(true);
        } else if (dy > delta) {
          setVisible(false);
        } else if (dy < -delta) {
          setVisible(true);
        }
        last = y;
        raf = 0;
      });
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [topThreshold, bottomThreshold, delta]);

  return visible;
}
